import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { empleadosAPI, usuariosAPI, movimientosEmpleadosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { ExportActions } from '../components/ExportActions';
import {
  exportFromColumnas,
  valorCeldaEmpleadoExport,
  imprimirListadoColumnas,
} from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';
import '../Clientes.css';

const CLAVE_PREFERENCIAS = 'empleados_listado';
const CLAVE_FILTROS = 'empleados_listado_filtros';
const COLUMNAS_DEFINICION = [
  { id: 'nombre', label: 'Nombre' },
  { id: 'telefono', label: 'Teléfono' },
  { id: 'fecha_nacimiento', label: 'Fecha nacimiento' },
  { id: 'sueldo', label: 'Sueldo' },
  { id: 'estado', label: 'Estado' },
];
const defaultColumnasVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosValores = {
  nombre: '',
  telefono: '',
  activo: 'true',
};

const Empleados = () => {
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const primeraCargaEmpleadosRef = useRef(true);
  const [refrescandoEmpleados, setRefrescandoEmpleados] = useState(false);
  const cargaEmpleadosSeqRef = useRef(0);
  const [filtrosValores, setFiltrosValores] = useState(() => loadFiltrosSession(CLAVE_FILTROS, defaultFiltrosValores));
  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisibles }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisibles }));
  const [, setLoadingPreferencias] = useState(true);
  const [savingPreferencias, setSavingPreferencias] = useState(false);
  const { hasPermission } = useAuth();
  const { logoUrl } = useBranding();
  const { fechaHoy, formatFecha: formatFechaCtx } = useZonaHoraria();
  const [showDevengacion, setShowDevengacion] = useState(false);
  const [devengMes, setDevengMes] = useState(() => new Date().getMonth() + 1);
  const [devengAnio, setDevengAnio] = useState(() => new Date().getFullYear());

  useEffect(() => {
    saveFiltrosSession(CLAVE_FILTROS, filtrosValores);
  }, [filtrosValores]);
  const [devengando, setDevengando] = useState(false);

  const colVisible = (id) => columnasVisibles[id] !== false;
  const columnasVisiblesIds = useMemo(
    () => COLUMNAS_DEFINICION.filter((c) => colVisible(c.id)).map((c) => c.id),
    [columnasVisibles]
  );
  const filtroVisible = useCallback((colId) => filtrosVisibles[colId] !== false && columnasVisibles[colId] !== false, [filtrosVisibles, columnasVisibles]);

  const setFiltro = (key, value) => setFiltrosValores(prev => ({ ...prev, [key]: value }));

  const loadEmpleados = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaEmpleadosRef.current;
    const seq = ++cargaEmpleadosSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescandoEmpleados(true);
      const params = {};
      if (filtroVisible('nombre') && filtrosValores.nombre) params.nombre = filtrosValores.nombre;
      if (filtroVisible('telefono') && filtrosValores.telefono) params.telefono = filtrosValores.telefono;
      if (filtroVisible('estado')) params.activo = filtrosValores.activo;
      else params.activo = 'true';
      const response = await empleadosAPI.getAll(params);
      if (seq !== cargaEmpleadosSeqRef.current) return;
      if (response.success) {
        setEmpleados(response.data);
      }
    } catch (error) {
      if (seq !== cargaEmpleadosSeqRef.current) return;
      toast.error('Error al cargar empleados');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaEmpleadosRef.current = false;
      }
      if (seq === cargaEmpleadosSeqRef.current) {
        setRefrescandoEmpleados(false);
      }
    }
  }, [filtrosValores, filtroVisible]);

  useEffect(() => {
    loadEmpleados();
  }, [loadEmpleados]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreferencias(true);
      try {
        const res = await usuariosAPI.getPreferencias(CLAVE_PREFERENCIAS);
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data[0] : res.data;
        const data = raw?.valor ?? null;
        if (data && typeof data === 'object') {
          if (data.columnasVisibles && typeof data.columnasVisibles === 'object') {
            setColumnasVisibles(prev => ({ ...defaultColumnasVisibles, ...data.columnasVisibles }));
          }
          if (data.filtrosVisibles && typeof data.filtrosVisibles === 'object') {
            setFiltrosVisibles(prev => ({ ...defaultFiltrosVisibles, ...data.filtrosVisibles }));
          }
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar preferencias:', e);
      } finally {
        if (!cancelled) setLoadingPreferencias(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const guardarPreferenciasDesdeLista = useCallback(async () => {
    setSavingPreferencias(true);
    try {
      await usuariosAPI.savePreferencias(CLAVE_PREFERENCIAS, {
        columnasVisibles: { ...columnasVisibles },
        filtrosVisibles: { ...filtrosVisibles },
      });
      toast.success('Preferencias de columnas y filtros guardadas.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar preferencias');
    } finally {
      setSavingPreferencias(false);
    }
  }, [columnasVisibles, filtrosVisibles]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este empleado?')) return;
    try {
      const response = await empleadosAPI.delete(id);
      if (response.success) {
        toast.success('Empleado eliminado');
        loadEmpleados();
      }
    } catch (error) {
      toast.error(error.message || 'Error al eliminar');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return formatFechaCtx(fecha);
  };

  const formatSueldo = (val) => {
    if (val == null || val === '') return '-';
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(val));
  };

  const handleExportListado = async (format) => {
    try {
      await exportFromColumnas({
        filename: 'empleados',
        sheetName: 'Empleados',
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: columnasVisiblesIds,
        items: empleados,
        getCellValue: (emp, colId) => valorCeldaEmpleadoExport(emp, colId, formatFecha),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const generarPDFListado = () => {
    try {
      imprimirListadoColumnas({
        titulo: 'Listado de empleados',
        subtitulo: `Generado el ${formatFechaCtx(fechaHoy())}`,
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: columnasVisiblesIds,
        items: empleados,
        getCellValue: (emp, colId) => valorCeldaEmpleadoExport(emp, colId, formatFecha),
        logoUrl,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Empleados</h1>
        {hasPermission('crear_clientes') && (
          <Link to="/empleados/nuevo" className="btn-primary">+ Nuevo empleado</Link>
        )}
        {hasPermission('devengar_sueldos') && (
          <button type="button" className="btn-secondary" style={{ marginLeft: 8 }} onClick={() => setShowDevengacion(true)}>
            Devengar sueldos
          </button>
        )}
      </div>

      {showDevengacion && (
        <div className="modal-overlay" onClick={() => setShowDevengacion(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Devengación masiva de sueldos</h2>
            <p style={{ color: '#666' }}>Crea un débito por empleado activo con sueldo &gt; 0. Anti-duplicado por mes/año.</p>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <label>Mes <input type="number" min={1} max={12} value={devengMes} onChange={(e) => setDevengMes(Number(e.target.value))} /></label>
              <label>Año <input type="number" min={2000} max={2100} value={devengAnio} onChange={(e) => setDevengAnio(Number(e.target.value))} /></label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-primary" disabled={devengando} onClick={async () => {
                setDevengando(true);
                try {
                  const res = await movimientosEmpleadosAPI.devengacionSueldo({
                    empleado_ids: empleados.filter((e) => e.activo).map((e) => e.id),
                    mes: devengMes,
                    anio: devengAnio,
                    fecha: fechaHoy(),
                  });
                  if (res.success) { toast.success(res.message); setShowDevengacion(false); }
                  else toast.error(res.message);
                } catch (e) { toast.error(e.message); }
                finally { setDevengando(false); }
              }}>
                {devengando ? 'Procesando...' : 'Confirmar devengación'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowDevengacion(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center' }}>
          {filtroVisible('nombre') && (
            <input
              type="text"
              placeholder="Nombre"
              value={filtrosValores.nombre}
              onChange={(e) => setFiltro('nombre', e.target.value)}
              className="search-input"
              style={{ maxWidth: 180 }}
            />
          )}
          {filtroVisible('telefono') && (
            <input
              type="text"
              placeholder="Teléfono"
              value={filtrosValores.telefono}
              onChange={(e) => setFiltro('telefono', e.target.value)}
              className="search-input"
              style={{ maxWidth: 140 }}
            />
          )}
          {filtroVisible('estado') && (
            <select value={filtrosValores.activo} onChange={(e) => setFiltro('activo', e.target.value)} title="Estado">
              <option value="true">Solo activos</option>
              <option value="false">Solo inactivos</option>
              <option value="">Todos</option>
            </select>
          )}
        </div>

        <div className="filters-toolbar filters-toolbar--acciones">
          <button type="button" className="btn-action-icon" onClick={() => setFiltrosValores({ ...defaultFiltrosValores })} title="Limpiar todos los filtros">
            <span aria-hidden="true">🧹</span>
          </button>
          <SelectorColumnas
            columnas={COLUMNAS_DEFINICION}
            visibleIds={Object.keys(columnasVisibles).filter(id => columnasVisibles[id])}
            onToggle={(id) => setColumnasVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
            onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisibles })}
            onSave={guardarPreferenciasDesdeLista}
            titulo="Columnas visibles"
            labelBoton="Columnas"
          />
          <SelectorFiltros
            filtros={COLUMNAS_DEFINICION}
            visibleIds={Object.keys(filtrosVisibles).filter(id => filtrosVisibles[id])}
            onToggle={(id) => setFiltrosVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
            onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisibles })}
            onSave={guardarPreferenciasDesdeLista}
            titulo="Filtros visibles"
            labelBoton="Filtros"
          />
          {empleados.length > 0 && (
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDFListado}
              printLabel="PDF"
            />
          )}
        </div>

        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className={`listado-tabla-area clientes-listado-tabla-area${refrescandoEmpleados ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {colVisible('nombre') && <th>Nombre</th>}
              {colVisible('telefono') && <th>Teléfono</th>}
              {colVisible('fecha_nacimiento') && <th>Fecha nacimiento</th>}
              {colVisible('sueldo') && <th>Sueldo</th>}
              {colVisible('estado') && <th>Estado</th>}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {empleados.length === 0 ? (
              <tr>
                <td colSpan={1 + COLUMNAS_DEFINICION.filter(c => colVisible(c.id)).length} className="no-data">No hay empleados que coincidan con el filtro</td>
              </tr>
            ) : (
              empleados.map(emp => (
                <tr key={emp.id}>
                  {colVisible('nombre') && <td><strong>{emp.nombre}</strong></td>}
                  {colVisible('telefono') && <td>{emp.telefono || '-'}</td>}
                  {colVisible('fecha_nacimiento') && <td>{formatFecha(emp.fecha_nacimiento)}</td>}
                  {colVisible('sueldo') && <td>{formatSueldo(emp.sueldo)}</td>}
                  {colVisible('estado') && (
                    <td>
                      <span className={`badge ${emp.activo ? 'activo' : 'inactivo'}`}>
                        {emp.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  )}
                  <td>
                    <div className="action-buttons">
                      {hasPermission('ver_movimientos_empleados') && (
                        <Link to={`/empleados/${emp.id}/movimientos`} className="btn-edit" title="Ver movimientos">
                          Movimientos
                        </Link>
                      )}
                      {hasPermission('editar_clientes') && (
                        <>
                          <Link to={`/empleados/editar/${emp.id}`} className="btn-edit">Editar</Link>
                          <button onClick={() => handleDelete(emp.id)} className="btn-delete">Eliminar</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
            </div>
          {refrescandoEmpleados && (
            <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
              <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
              <span className="clientes-listado-refresh-label">Actualizando…</span>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Empleados;
